import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { AuthService } from '../service/auth.service';
import { TokenStorageService } from '@services/token-storage.service';
import { Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {

  constructor(private authService: AuthService, private router: Router, private tokenStorage: TokenStorageService) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const token = TokenStorageService.getToken();
    const currentRoute = state.url;
    if (!token) {
      this.router.navigate(['/sign-in'], { queryParams: { returnUrl: currentRoute } });
      return of(false);
    }

    return this.authService.verifyAccess(currentRoute).pipe(
      tap(isAuth => {
        if (!isAuth) {
          this.router.navigate(['/sign-in'], { queryParams: { returnUrl: currentRoute } });
        }
      }),
      catchError((error) => {
        console.error('Error during authentication:', error);
        this.router.navigate(['/sign-in'], { queryParams: { returnUrl: currentRoute } });
        return of(false);
      })
    );
  }
}


@Injectable({
  providedIn: 'root'
})
export class EnterpriseAuthGuard implements CanActivate {

  constructor(private authService: AuthService, private router: Router, private tokenStorage: TokenStorageService) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const token = TokenStorageService.getToken();
    const currentRoute = state.url;
    const taxId = currentRoute.split("/")[2];
    if (!token) {
      this.router.navigate(['/sign-in'], { queryParams: { returnUrl: currentRoute } });
      return of(false);
    }

    return this.authService.verifyEnterpriseAccess(taxId, currentRoute).pipe(
      tap(isAuth => {
        if (!isAuth) {
          this.router.navigate(['/sign-in'], { queryParams: { returnUrl: currentRoute } });
        }
      }),
      catchError((error) => {
        console.error('Error during authentication:', error);
        this.router.navigate(['/sign-in'], { queryParams: { returnUrl: currentRoute } });
        return of(false);
      })
    );
  }
}


@Injectable({
  providedIn: 'root'
})
export class TeamAuthGuard implements CanActivate {

  constructor(private authService: AuthService, private router: Router, private tokenStorage: TokenStorageService) {}

  /** /team/:team_code/... 的 team_code 可能在父層，不能只用 split("/")[2] */
  private getTeamCodeFromSnapshot(snap: ActivatedRouteSnapshot | null): string {
    let s: ActivatedRouteSnapshot | null = snap;
    while (s) {
      const t = s.paramMap.get('team_code');
      if (t) {
        return t;
      }
      s = s.parent;
    }
    return '';
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const token = TokenStorageService.getToken();
    const currentRoute = state.url;
    const team_code = this.getTeamCodeFromSnapshot(next) || state.url.split('/').filter(Boolean)[1];
    if (!token) {
      this.router.navigate(['/sign-in'], { queryParams: { returnUrl: currentRoute } });
      return of(false);
    }
    if (!team_code) {
      console.error('TeamAuthGuard: 無法從路由取得 team_code', state.url);
      this.router.navigate(['/sign-in'], { queryParams: { returnUrl: currentRoute } });
      return of(false);
    }

    return this.authService.verifyTeamAccess(team_code, currentRoute).pipe(
      tap(isAuth => {
        if (!isAuth) {
          this.router.navigate(['/sign-in'], { queryParams: { returnUrl: currentRoute } });
        }
      }),
      catchError((error) => {
        console.error('Error during authentication:', error);
        this.router.navigate(['/sign-in'], { queryParams: { returnUrl: currentRoute } });
        return of(false);
      })
    );
  }
}


@Injectable({
  providedIn: 'root'
})
export class TeamApplicationAuthGuard implements CanActivate {

  constructor(private authService: AuthService, private router: Router, private tokenStorage: TokenStorageService) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const token = TokenStorageService.getToken();
    const currentRoute = state.url;
    const team_code = currentRoute.split("/")[2];
    const appId = currentRoute.split("/")[4];
    if (!token) {
      this.router.navigate(['/sign-in'], { queryParams: { returnUrl: currentRoute } });
      return of(false);
    }

    return this.authService.verifyTeamApplicationAccess(team_code, Number(appId)).pipe(
      tap(isAuth => {
        if (!isAuth) {
          this.router.navigate(['/sign-in'], { queryParams: { returnUrl: currentRoute } });
        }
      }),
      catchError((error) => {
        console.error('Error during authentication:', error);
        this.router.navigate(['/sign-in'], { queryParams: { returnUrl: currentRoute } });
        return of(false);
      })
    );
  }
}


@Injectable({
  providedIn: 'root'
})
export class ApplicationAuthGuard implements CanActivate {

  constructor(private authService: AuthService, private router: Router, private tokenStorage: TokenStorageService) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const token = TokenStorageService.getToken();
    const currentRoute = state.url;
    const appId = currentRoute.split("/")[2];
    if (!token) {
      this.router.navigate(['/sign-in'], { queryParams: { returnUrl: currentRoute } });
      return of(false);
    }

    return this.authService.verifyApplicationAccess(Number(appId)).pipe(
      tap(isAuth => {
        if (!isAuth) {
          this.router.navigate(['/sign-in'], { queryParams: { returnUrl: currentRoute } });
        }
      }),
      catchError((error) => {
        console.error('Error during authentication:', error);
        this.router.navigate(['/sign-in'], { queryParams: { returnUrl: currentRoute } });
        return of(false);
      })
    );
  }
}
